import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import Logo from '../../components/common/Logo';

const ResetPassword = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch
  } = useForm();

  // Watch password to validate confirmation
  const password = watch('password');

  useEffect(() => {
    // Check if token exists
    if (!token) {
      setTokenValid(false);
      return;
    }
    
    // For now, assume token is valid if it exists
    // In a real scenario, you might want to validate the token with the backend
    setTokenValid(true);
  }, [token]);

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error(t('resetPassword.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      // Call API to reset password with token
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/resetpassword/${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: data.password
        }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(t('resetPassword.passwordChangeSuccess'));
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast.error(result.message || t('resetPassword.passwordChangeError'));
        if (result.message?.includes('غير صالح') || result.message?.includes('منتهي')) {
          setTokenValid(false);
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(t('resetPassword.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking token
  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('resetPassword.checkingToken')}</p>
        </div>
      </div>
    );
  }

  // Show error state for invalid token
  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <Logo size="xl" animated={true} />
            </Link>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              {t('resetPassword.invalidTokenTitle')}
            </h2>
          </div>

          <div className="bg-white py-8 px-6 shadow-xl rounded-2xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('resetPassword.invalidTokenTitle')}
              </h3>
              
              <div className="space-y-3 text-sm text-gray-600 mb-6">
                <p>{t('resetPassword.invalidTokenMessage')}</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-xs">
                    {t('resetPassword.tokenExpiryNote')}
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                <Link
                  to="/forgot-password"
                  className="w-full bg-brand-blue text-white py-3 px-4 rounded-lg hover:bg-brand-blueSecondary transition-colors block text-center"
                >
                  {t('resetPassword.requestNewLink')}
                </Link>
                <Link
                  to="/login"
                  className="block w-full text-center text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center gap-2"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  {t('resetPassword.backToLogin')}
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <Logo size="xl" animated={true} />
          </Link>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {t('resetPassword.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('resetPassword.subtitle')}
          </p>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white py-8 px-6 shadow-xl rounded-2xl"
        >
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
              <p className="text-green-800 text-sm">
                {t('resetPassword.tokenVerifiedMessage')}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* New Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('resetPassword.newPasswordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: t('resetPassword.newPasswordRequired'),
                    minLength: {
                      value: 6,
                      message: t('resetPassword.newPasswordMinLength'),
                    },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className={`block w-full pr-10 pl-10 py-3 border ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent`}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 left-0 pl-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                {t('resetPassword.confirmPasswordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('confirmPassword', {
                    required: t('resetPassword.confirmPasswordRequired'),
                    validate: (value) =>
                      value === password || t('resetPassword.passwordMismatch'),
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`block w-full pr-10 pl-10 py-3 border ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent`}
                  placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 left-0 pl-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">{t('resetPassword.passwordRequirements')}</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${password && password.length >= 6 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  {t('resetPassword.requirement6Chars')}
                </li>
                <li className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${password && getValues('confirmPassword') && password === getValues('confirmPassword') ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  {t('resetPassword.requirementMatch')}
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-brand-blue to-brand-blueSecondary hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <KeyIcon className="h-5 w-5 ml-2" />
                  {t('resetPassword.changePasswordButton')}
                </>
              )}
            </motion.button>

            {/* Back to Login */}
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-brand-blue hover:text-brand-blueSecondary flex items-center justify-center gap-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                {t('resetPassword.backToLogin')}
              </Link>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;